import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../stores/store';
import { NAV_ROUTES } from '../../constants/navRoutes';
import { buildSubmoduleUrl, getDashboardDataBasedOnUrl, toSubmodulePath } from '@utils/common';
import EmptyDashboard from './EmptyDashboard';
import { FullPageLoader } from '@components';
import SupersetDashboard from './SupersetDashboard';
import CustomDashboardWrapper from './CustomDashboardWrapper';
import { MODULES_REQUEST_STATUS } from '@utils/types/RequestedDashboards';
import { SESSION_STORAGE_KEYS, SUBMODULE_TYPE, SubmoduleCategory } from '@constants/constants';
import { SessionStorage } from '@utils/storage';
import { clearDeepLinkStorage, getDeepLinkSubmodule } from '../DeepLinkRedirect/utils';

const Dashboard: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    module,
    submodule: subModule,
    id,
  } = useParams<{
    module?: string;
    submodule?: string;
    id?: string;
  }>();
  const [searchParams] = useSearchParams();
  const modulesInStore = useSelector((state: RootState) => state?.ModulesData);
  const [isNoDashboardAccess, setNoDashboardAccess] = useState(false);
  const [isEmptyDashboard, setEmptyDashboard] = useState(false);
  const [dashboardData, setDashboardData] = useState<{
    id: string;
    embedId: string;
    submoduleType?: SUBMODULE_TYPE;
    filterQuery: string;
    requestStatus: MODULES_REQUEST_STATUS;
    requestId?: string;
    submoduleCategory: SubmoduleCategory;
  } | null>(null);

  // Hook to set dashboard redirections
  useEffect(() => {
    // If no modules are loaded, wait for fetch
    if (modulesInStore.loadCount === 0) return;

    // A deep link from the website takes priority over the url and the default dashboard
    if (redirectToDeepLinkedSubmodule()) return;

    // If no modules are selected in url, redirect to default dashbaord
    if (!module) {
      redirectToDefaultDashboard();
      return;
    }

    const subModulePath = subModule || location.pathname;
    const updatedDashboardData = getDashboardDataBasedOnUrl(modulesInStore.modules, subModulePath);

    setNoDashboardAccess(!updatedDashboardData);
    setDashboardData(updatedDashboardData);
  }, [module, modulesInStore, subModule]);

  // Function to redirect to the submodule stored by the session storage. Runs once, as the key is
  // removed here. Returns false when there is nothing to redirect to.
  const redirectToDeepLinkedSubmodule = (): boolean => {
    const submoduleType = getDeepLinkSubmodule();
    if (!submoduleType) {
      SessionStorage.remove(SESSION_STORAGE_KEYS.SUBMODULE_TO_REDIRECT);
      return false;
    }

    for (const userModule of modulesInStore.modules ?? []) {
      const submodule = userModule.submodules?.find((item) => item.submoduleType === submoduleType);
      if (submodule) {
        const route = buildSubmoduleUrl(userModule.name, submodule.url);
        // Already on it: let the normal flow below render it
        if (route === location.pathname) {
          SessionStorage.remove(SESSION_STORAGE_KEYS.SUBMODULE_TO_REDIRECT);
          return false;
        }
        navigate(route, { replace: true });
        return true;
      }
    }

    // User has no access to the submodule, so its params would never be read
    clearDeepLinkStorage(submoduleType);
    return false;
  };

  // Function to redirect to first dashboard in access of user
  const redirectToDefaultDashboard = () => {
    const redirectModule = modulesInStore?.modules?.[0];

    if (!redirectModule || module) {
      setEmptyDashboard(true);
      return;
    }

    const urlModuleParam = toSubmodulePath(redirectModule.name);
    const redirectSubmodule = redirectModule.submodules?.[0];

    const route = redirectSubmodule
      ? `${NAV_ROUTES.DASHBOARD}/${urlModuleParam}/${redirectSubmodule.url}`
      : `${NAV_ROUTES.DASHBOARD}/${urlModuleParam}`;

    navigate(route);
  };

  // If no dashboards present for user display empty dashboard view
  if (isEmptyDashboard) {
    return <EmptyDashboard all={true} />;
  }

  // If a particular dashboard access is not present for user
  if (isNoDashboardAccess) {
    return <EmptyDashboard />;
  }

  if (!dashboardData || !dashboardData?.id) {
    return <FullPageLoader hasSideNav={true} />;
  }

  if (dashboardData.submoduleCategory === SubmoduleCategory.SUPERSET) {
    return <SupersetDashboard data={dashboardData} />;
  }

  if (dashboardData.submoduleCategory === SubmoduleCategory.NATIVE) {
    return <CustomDashboardWrapper id={id} data={dashboardData} urlParams={searchParams} />;
  }

  return null;
};

export default Dashboard;
